import { MarkdownText } from '../markdown-text';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '../dialogs/dialog';
import { ExplainedButton as Button } from '../explained-button';
import { Dropdown } from 'primereact/dropdown';
import { ManagedFragmentDto, ModuleDto } from '../../general/dtos';
import { fragmentContentsDiffer, moduleContentsDiffer, ModuleRevision } from '../../general/module-history';
import { useApi } from '../../providers/api-provider';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';
import { HtmlViewer } from '../htmlViewer';
import { renderFragmentHtml } from '../content-viewer/rendering-helpers';
import '../../assets/module-history.css';

const CURRENT_DRAFT = 'current';

type VersionOption = { value: string; label: string };

export function ModuleHistoryDialog({ module, onClose, onRestore, onRestoreFragment }: {
    module: ModuleDto; onClose: () => void; onRestore: (snapshot: ModuleDto) => void;
    onRestoreFragment?: (snapshot: ModuleDto, fragmentKey: string) => void;
}) {
    const api = useApi();
    const parent = useFullscreenDialogParent();
    // Compare a stable copy of the editor, without writing a revision or reloading saved content.
    const [draft] = useState(() => structuredClone(module));
    const [rows, setRows] = useState<ModuleRevision[]>([]);
    const [more, setMore] = useState(false);
    const [before, setBefore] = useState<string>();
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [left, setLeft] = useState<string | null>(null);
    const [right, setRight] = useState<string | null>(CURRENT_DRAFT);

    const [fragmentKey, setFragmentKey] = useState<string | null>(draft.fragments?.[0]?.key ?? null);
    const [loaded, setLoaded] = useState<Partial<Record<'left' | 'right', { version: string; module: ModuleDto }>>>({});
    const snapshotLoaded = useCallback((side: 'left' | 'right', version: string, snapshot: ModuleDto) => {
        setLoaded(current => ({ ...current, [side]: { version, module: snapshot } }));
        setFragmentKey(current => current ?? snapshot.fragments?.[0]?.key ?? null);
    }, []);
    const comparison = useMemo(() => {
        const snapshots = [left === CURRENT_DRAFT ? draft : loaded.left?.version === left ? loaded.left.module : null,
            right === CURRENT_DRAFT ? draft : loaded.right?.version === right ? loaded.right.module : null];
        const union = new Map<string, ManagedFragmentDto>();
        snapshots.forEach(snapshot => snapshot?.fragments?.forEach(fragment => {
            if (!union.has(fragment.key)) union.set(fragment.key, fragment);
        }));
        const changedKeys = new Set<string>();
        if (snapshots[0] && snapshots[1]) {
            const leftFragments = new Map((snapshots[0].fragments ?? []).map(fragment => [fragment.key, fragment]));
            const rightFragments = new Map((snapshots[1].fragments ?? []).map(fragment => [fragment.key, fragment]));
            union.forEach((_, key) => { if (fragmentContentsDiffer(leftFragments.get(key), rightFragments.get(key))) changedKeys.add(key); });
        }
        return { fragments: [...union.values()], changedKeys };
    }, [draft, left, right, loaded]);

    useEffect(() => {
        if (!api) return;
        const abort = new AbortController();
        setLoading(true); setError('');
        api.getModuleHistory(module.module_key, before, abort.signal).then(result => {
            if (abort.signal.aborted) return;
            setRows(current => before ? [...current, ...result.data.filter(row => !current.some(old => old.id === row.id))] : result.data);
            setMore(result.has_more);
            if (!before) setLeft(current => current ?? result.data[0]?.id ?? CURRENT_DRAFT);
        }).catch(failure => { if (!abort.signal.aborted) setError(failure?.response?.data?.message || 'Could not load revision history.'); })
            .finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [api, module.module_key, before, refresh]);

    const options: VersionOption[] = [{ value: CURRENT_DRAFT, label: 'Current editor draft (includes unsaved changes)' },
        ...rows.map(row => ({ value: row.id,
            label: `#${row.id} - ${row.created_at} UTC - ${row.created_by_name || 'Unknown administrator'}` }))];
    const selector = (side: 'left' | 'right', value: string | null, change: (value: string) => void) => <div className="module-history-version">
        <label htmlFor={`history-${side}-version`}>{side === 'left' ? 'Left version' : 'Right version'}</label>
        <Dropdown inputId={`history-${side}-version`} aria-label={`${side === 'left' ? 'Left' : 'Right'} version`} value={value} options={options}
            placeholder="Select a version" onChange={event => change(event.value)} appendTo={parent} className="w-full"
            pt={{ trigger: { 'aria-label': `Choose ${side} version` } }} />
    </div>;

    return <Dialog header={`History - ${module.name}`} visible modal appendTo={parent} className="module-history-dialog"
        style={{ width: '96vw', maxWidth: '110rem' }} draggable={false} onHide={onClose}
        footer={<Button label="Close" text onClick={onClose} />}>
        <p className="mt-0">Both panes are read-only previews. Restore copies the selected version{onRestoreFragment ? ' or fragment' : ''} into your current editor draft. Saved history stays unchanged. Review the restored draft, then Save or Cancel.</p>
        <div className="module-history-toolbar">
            {selector('left', left, setLeft)}
            {selector('right', right, setRight)}
        </div>
        <div className="module-history-status">
            <span>* Fragment content or settings differ between the selected versions.</span>
            {error && <><span role="alert">{error}</span><Button label="Retry history" text disabled={loading} onClick={() => setRefresh(value => value + 1)} /></>}
            {loading && <span role="status">Loading history...</span>}
            {!loading && !error && !rows.length && <span>No revisions yet. History starts with the first save after this feature was enabled.</span>}
            {more && !error && <Button label="Load older revisions" text disabled={loading} onClick={() => setBefore(rows[rows.length - 1]?.id)} />}
        </div>
        <div className="module-history-layout">
            <section className="module-history-preview" aria-label="Left preview"><RevisionPreview key={`left:${left}`} side="left" version={left} row={rows.find(row => row.id === left)} draft={draft} onRestore={onRestore} fragmentKey={fragmentKey} onFragmentChange={setFragmentKey}
                comparisonFragments={comparison.fragments} changedFragmentKeys={comparison.changedKeys} onSnapshotLoaded={snapshotLoaded} onRestoreFragment={onRestoreFragment} /></section>
            <section className="module-history-preview" aria-label="Right preview"><RevisionPreview key={`right:${right}`} side="right" version={right} row={rows.find(row => row.id === right)} draft={draft} onRestore={onRestore} fragmentKey={fragmentKey} onFragmentChange={setFragmentKey}
                comparisonFragments={comparison.fragments} changedFragmentKeys={comparison.changedKeys} onSnapshotLoaded={snapshotLoaded} onRestoreFragment={onRestoreFragment} /></section>
        </div>
    </Dialog>;
}

function RevisionPreview({ side, version, row, draft, onRestore, fragmentKey, onFragmentChange, comparisonFragments, changedFragmentKeys, onSnapshotLoaded, onRestoreFragment }: {
    side: 'left' | 'right'; version: string | null; row?: ModuleRevision; draft: ModuleDto; onRestore: (snapshot: ModuleDto) => void;
    fragmentKey: string | null; onFragmentChange: (key: string) => void; comparisonFragments: ManagedFragmentDto[]; changedFragmentKeys: Set<string>;
    onSnapshotLoaded: (side: 'left' | 'right', version: string, snapshot: ModuleDto) => void;
    onRestoreFragment?: (snapshot: ModuleDto, fragmentKey: string) => void;
}) {
    const api = useApi();
    const parent = useFullscreenDialogParent();
    const isDraft = version === CURRENT_DRAFT;
    const [snapshot, setSnapshot] = useState<ModuleDto | null>(isDraft ? draft : null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(!isDraft && !!version);
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        if (isDraft || !version || !api) return;
        const abort = new AbortController();
        setLoading(true); setError(''); setSnapshot(null);
        api.getModuleRevision(draft.module_key, version, abort.signal).then(result => {
            if (abort.signal.aborted) return;
            if (result.schema_version !== 1 || result.module.module_key !== draft.module_key) throw new Error('Unsupported module revision.');
            setSnapshot(result.module);
            onSnapshotLoaded(side, version, result.module);
        }).catch(failure => { if (!abort.signal.aborted) setError(failure?.response?.data?.message || failure?.message || 'Could not load this revision.'); })
            .finally(() => { if (!abort.signal.aborted) setLoading(false); });
        return () => abort.abort();
    }, [api, draft.module_key, version, isDraft, retry, side, onSnapshotLoaded]);

    const ownFragments = new Map((snapshot?.fragments ?? []).map(item => [item.key, item]));
    const fragment = fragmentKey ? ownFragments.get(fragmentKey) : undefined;
    const ownPositions = new Map((snapshot?.fragments ?? []).map((item, index) => [item.key, index + 1]));
    const fragmentOptions = comparisonFragments.map(item => {
        const own = ownFragments.get(item.key);
        return { value: item.key, label: own ? `${ownPositions.get(item.key)}. ${own.title || 'Untitled'}${own.hidden ? ' (hidden)' : ''}${changedFragmentKeys.has(item.key) ? ' *' : ''}`
            : `-- missing -- (${item.title || item.key})${changedFragmentKeys.has(item.key) ? ' *' : ''}` };
    });
    if (fragmentKey && !fragmentOptions.some(option => option.value === fragmentKey))
        fragmentOptions.push({ value: fragmentKey, label: '-- missing --' });
    const rendered = useMemo(() => {
        try {
            return { html: snapshot?.is_managed ? (fragment ? renderFragmentHtml(fragment.html, fragment.formFields ?? [], {
                fragments: snapshot.fragments ?? [], currentKey: fragment.key, answers: {},
            }) : '') : snapshot?.content ?? '', error: '' };
        } catch { return { html: '', error: 'This revision could not be rendered. Its saved data has not been changed.' }; }
    }, [snapshot, fragment]);
    const canRestore = !isDraft && snapshot && !loading && !error && !!snapshot.is_managed === !!draft.is_managed;
    const restoreBlocked = isDraft ? 'This is the current editor draft. Select a saved revision to restore.'
        : !version ? 'Select a saved revision to restore.'
        : loading ? 'Wait for this revision to finish loading.'
        : error || !snapshot ? 'Retry loading this revision before restoring it.'
        : !!snapshot.is_managed !== !!draft.is_managed ? 'This revision uses a different editor type and cannot be restored here.' : undefined;
    const versionDiffers = useMemo(() => snapshot ? moduleContentsDiffer(snapshot, draft) : false, [snapshot, draft]);
    const fragmentDiffers = useMemo(() => fragmentContentsDiffer(fragment, draft.fragments?.find(item => item.key === fragmentKey)), [fragment, draft, fragmentKey]);
    const theme = typeof snapshot?.theme === 'string' ? snapshot.theme : snapshot?.theme?.[0]?.value ?? '';
    const title = side === 'left' ? 'Left preview' : 'Right preview';

    return <div className="module-history-preview-content" aria-busy={loading}>
        <div className="module-history-preview-heading">
            <strong>{isDraft ? 'Current editor draft' : version ? `Revision #${version}` : 'Select a version'}</strong>
            <Button label="Restore module to editor" aria-label={`Restore module to editor from ${side} version`} icon="pi pi-history" text disabled={!canRestore || !versionDiffers}
                disabledReason={restoreBlocked || (!versionDiffers ? 'This version already matches the current editor draft.' : undefined)}
                title={canRestore && !versionDiffers ? 'This version already matches the current editor draft.' : 'Load this entire version into the current editor draft, including all fragments and module settings. The other preview and saved history stay unchanged. Review the draft before saving.'}
                onClick={() => { if (canRestore && versionDiffers) onRestore(snapshot!); }} />
        </div>
        {loading && <p role="status">Loading revision...</p>}
        {error && <><p role="alert">{error}</p><Button label={`Retry ${side} preview`} outlined onClick={() => setRetry(value => value + 1)} /></>}
        {snapshot && !loading && <>
            <h3>{snapshot.name}</h3>
            <MarkdownText text={snapshot.description} />
            <small>{isDraft ? 'Includes unsaved edits. This copy is not stored in history.' : `Archived by ${row?.created_by_name || 'Unknown administrator'} - Saved ${snapshot.updated_at} UTC`}</small>
            {!!snapshot.is_managed !== !!draft.is_managed && <p role="alert">This revision uses a different editor type and cannot be restored here.</p>}
            {snapshot.is_managed && <div className="module-history-fragment">
                <label htmlFor={`history-${side}-fragment`}>Fragment</label>
                <Dropdown inputId={`history-${side}-fragment`} aria-label={`${side} fragment`} value={fragmentKey}
                    pt={{ trigger: { 'aria-label': `Choose ${side} fragment` } }}
                    options={fragmentOptions}
                    onChange={event => onFragmentChange(event.value)} appendTo={parent} className="w-full" />
                {onRestoreFragment && <div className="module-history-fragment-restore">
                    <Button label="Restore fragment to editor" aria-label={`Restore fragment to editor from ${side} version`} icon="pi pi-history" outlined
                        disabled={!canRestore || !fragment || !fragmentDiffers}
                        disabledReason={restoreBlocked || (!fragment ? 'This fragment does not exist in this version. Select an available fragment.'
                            : !fragmentDiffers ? 'This fragment already matches the current editor draft.' : undefined)}
                        title={canRestore && fragment && !fragmentDiffers ? 'This fragment already matches the current editor draft.' : "Load this fragment's content, fields, actions and settings into the current editor draft. Other draft changes, the other preview and saved history stay unchanged. Review the draft before saving."}
                        onClick={() => { if (canRestore && fragment && fragmentDiffers) onRestoreFragment(snapshot!, fragment.key); }} />
                    <small>Other fragments and module settings stay as they are. Deleted fragments are added at the end.</small>
                </div>}
            </div>}
            {rendered.error ? <p role="alert">{rendered.error}</p> : <div className="module-history-html">
                {snapshot.is_managed && fragmentKey && !fragment
                    ? <div className="module-history-missing" role="status">-- missing -- This fragment does not exist in this version.</div>
                    : <HtmlViewer key={`${version}:${fragmentKey}`} htmlContent={rendered.html} theme={theme}
                    instructions={fragment?.instructions ?? snapshot.instructions ?? ''} selectedMode="preview" readOnly hideControls inputsReadOnly
                    sandboxStyles={!snapshot.is_managed} previewTitle={`${title}: ${isDraft ? 'current editor draft' : `revision ${version}`}`} />}
            </div>}
        </>}
    </div>;
}
